import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Alert, CircularProgress, IconButton, Tooltip,
  Checkbox, Divider, LinearProgress, Collapse
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { previewStakeholderImport, confirmStakeholderImport, downloadStakeholderTemplate } from '../utils/stakeholderService';

const STEPS = ['Upload File', 'Review & Resolve', 'Confirm'];

const ACTION_CONFIG = {
  create:         { label: 'New',             color: 'success', icon: <AddCircleOutlineIcon sx={{ fontSize: 14 }} /> },
  update:         { label: 'Update',          color: 'info',    icon: <EditNoteIcon sx={{ fontSize: 14 }} /> },
  unchanged:      { label: 'No Changes',      color: 'default', icon: null },
  file_duplicate: { label: 'Duplicate in file', color: 'warning', icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
};

const ActionChip = ({ action, changeCount }) => {
  const cfg = ACTION_CONFIG[action] || ACTION_CONFIG.create;
  const label = action === 'update' && changeCount
    ? `Update (${changeCount} field${changeCount > 1 ? 's' : ''})`
    : cfg.label;
  return (
    <Chip
      size="small"
      label={label}
      color={cfg.color}
      icon={cfg.icon}
      sx={{ fontSize: 11 }}
    />
  );
};

const PREVIEW_COLS = [
  { key: 'name',          label: 'Name' },
  { key: 'role',          label: 'Role' },
  { key: 'organization',  label: 'Organization' },
  { key: 'domain_module', label: 'Domain/Module' },
  { key: 'main_email',    label: 'Email' },
];

// Inline diff table shown when an update row is expanded
const DiffTable = ({ changes }) => {
  const entries = Object.entries(changes);
  if (!entries.length) return null;
  return (
    <Box sx={{ px: 2, pb: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'action.hover' }}>
            <TableCell sx={{ fontWeight: 600, width: '22%', py: 0.5 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '39%', py: 0.5, color: 'text.secondary' }}>Current value</TableCell>
            <TableCell sx={{ fontWeight: 600, width: '39%', py: 0.5, color: 'info.main' }}>New value</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([field, { label, old: oldVal, new: newVal }]) => (
            <TableRow key={field}>
              <TableCell sx={{ py: 0.5, fontWeight: 500 }}>{label}</TableCell>
              <TableCell sx={{ py: 0.5, color: 'text.secondary', fontStyle: oldVal ? 'normal' : 'italic' }}>
                {oldVal || '—'}
              </TableCell>
              <TableCell sx={{ py: 0.5, color: 'info.dark', fontWeight: 500 }}>
                {newVal || '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default function StakeholderImportDialog({ open, onClose, projectId, projectName, onImported }) {
  const [step, setStep]           = useState(0);
  const [file, setFile]           = useState(null);
  const [dragOver, setDragOver]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [rows, setRows]           = useState([]);
  const [excluded, setExcluded]   = useState(new Set());
  const [expanded, setExpanded]   = useState(new Set());  // row_index values with diff open
  const fileInputRef              = useRef();

  const reset = () => {
    setStep(0); setFile(null); setError('');
    setRows([]); setExcluded(new Set()); setExpanded(new Set());
  };

  const handleClose = () => { reset(); onClose(); };

  // ── Step 0 ───────────────────────────────────────────────────────────────
  const handleFileDrop = useCallback((e) => {
    e.preventDefault(); setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleFileChange = (e) => { if (e.target.files[0]) setFile(e.target.files[0]); };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true); setError('');
    const result = await previewStakeholderImport(projectId, file);
    setLoading(false);
    if (!result.success) { setError(result.error || 'Failed to parse file.'); return; }

    // Default: exclude "unchanged" and "file_duplicate" rows
    const autoExcluded = new Set(
      result.rows
        .filter(r => r.action === 'unchanged' || r.action === 'file_duplicate')
        .map(r => r.row_index)
    );
    setRows(result.rows);
    setExcluded(autoExcluded);
    setStep(1);
  };

  // ── Step 1 ───────────────────────────────────────────────────────────────
  const toggleExclude = (rowIndex) => {
    setExcluded(prev => {
      const next = new Set(prev);
      next.has(rowIndex) ? next.delete(rowIndex) : next.add(rowIndex);
      return next;
    });
  };

  const toggleExpand = (rowIndex) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(rowIndex) ? next.delete(rowIndex) : next.add(rowIndex);
      return next;
    });
  };

  const includedRows = rows.filter(r => !excluded.has(r.row_index));
  const newCount     = includedRows.filter(r => r.action === 'create').length;
  const updateCount  = includedRows.filter(r => r.action === 'update').length;

  // ── Step 2 ───────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setLoading(true); setError('');
    const stakeholders = includedRows.map(r => ({
      action:         r.action,
      data:           r.data,
      stakeholder_id: r.existing_match?.stakeholder_id || null,
    }));
    const result = await confirmStakeholderImport(projectId, stakeholders);
    setLoading(false);
    if (!result.success) { setError(result.error || 'Import failed.'); return; }
    onImported && onImported(result);
    reset();
    onClose();
  };

  const handleDownloadTemplate = async () => {
    try { await downloadStakeholderTemplate(projectId, projectName); }
    catch { setError('Could not download template.'); }
  };

  // ── Row background ────────────────────────────────────────────────────────
  const rowBg = (r) => {
    if (excluded.has(r.row_index)) return 'action.disabledBackground';
    if (r.action === 'update')     return 'rgba(2,136,209,0.05)';
    if (r.action === 'file_duplicate') return 'rgba(255,152,0,0.07)';
    return 'inherit';
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadFileIcon color="primary" />
          <Typography variant="h6">Import Stakeholder List</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Step 0 ── */}
        {step === 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }} icon={<InfoOutlinedIcon />}>
              Download the template, fill it in from row 3 onwards, then upload it here.
              Re-uploading with changes will show you a diff before you confirm.
            </Alert>

            <Button variant="outlined" startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate} sx={{ mb: 3 }}>
              Download Template
            </Button>

            <Box
              onDrop={handleFileDrop}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current.click()}
              sx={{
                border: `2px dashed ${dragOver ? '#1976d2' : '#bbb'}`,
                borderRadius: 2, p: 5, textAlign: 'center', cursor: 'pointer',
                backgroundColor: dragOver ? 'action.hover' : 'background.paper',
                transition: 'all 0.2s',
              }}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                {file ? file.name : 'Drag & drop your .xlsx file here, or click to select'}
              </Typography>
              {file && (
                <Chip label={file.name} onDelete={e => { e.stopPropagation(); setFile(null); }}
                  sx={{ mt: 1 }} color="primary" variant="outlined" />
              )}
              <input ref={fileInputRef} type="file" accept=".xlsx" hidden onChange={handleFileChange} />
            </Box>
          </Box>
        )}

        {/* ── Step 1 ── */}
        {step === 1 && (
          <Box>
            {/* Summary bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ mr: 1 }}>
                {rows.length} row{rows.length !== 1 ? 's' : ''} parsed
              </Typography>
              {(['create','update','unchanged','file_duplicate']).map(action => {
                const cnt = rows.filter(r => r.action === action).length;
                if (!cnt) return null;
                const cfg = ACTION_CONFIG[action];
                return (
                  <Chip key={action} size="small" label={`${cnt} ${cfg.label}`}
                    color={cfg.color} variant="outlined" />
                );
              })}
            </Box>

            {rows.some(r => r.action === 'update') && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Updates detected.</strong> Rows marked "Update" will overwrite the existing
                stakeholder's fields with the new values. Click the expand icon to review the exact changes
                before confirming.
              </Alert>
            )}

            {rows.some(r => r.action === 'file_duplicate') && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Some rows share the same name or email as an earlier row in the file and are unchecked by default.
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">Import</TableCell>
                    <TableCell sx={{ width: 32 }}>#</TableCell>
                    {PREVIEW_COLS.map(c => (
                      <TableCell key={c.key} sx={{ minWidth: 110 }}>{c.label}</TableCell>
                    ))}
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => {
                    const isExcl    = excluded.has(r.row_index);
                    const isExpanded = expanded.has(r.row_index);
                    const changeCount = Object.keys(r.changes || {}).length;
                    const hasChanges  = changeCount > 0;
                    return (
                      <React.Fragment key={r.row_index}>
                        <TableRow
                          sx={{
                            backgroundColor: rowBg(r),
                            opacity: isExcl ? 0.45 : 1,
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={!isExcl} onChange={() => toggleExclude(r.row_index)} />
                          </TableCell>
                          <TableCell>{r.row_index + 1}</TableCell>
                          {PREVIEW_COLS.map(c => (
                            <TableCell key={c.key}
                              sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {r.data[c.key] || '—'}
                            </TableCell>
                          ))}
                          <TableCell>
                            <ActionChip action={r.action} changeCount={changeCount} />
                            {r.action === 'file_duplicate' && r.file_dup_of !== null && (
                              <Tooltip title={`Same name/email as row ${r.file_dup_of + 1}`} arrow>
                                <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell padding="none">
                            {r.action === 'update' && hasChanges && (
                              <Tooltip title={isExpanded ? 'Hide changes' : 'Show changes'}>
                                <IconButton size="small" onClick={() => toggleExpand(r.row_index)}>
                                  {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>

                        {/* Expandable diff row */}
                        {r.action === 'update' && (
                          <TableRow sx={{ backgroundColor: 'rgba(2,136,209,0.03)' }}>
                            <TableCell colSpan={PREVIEW_COLS.length + 4} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <DiffTable changes={r.changes} />
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeleteOutlineIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                Unchecked rows are skipped. "No Changes" and "Duplicate in file" rows are unchecked by default.
              </Typography>
            </Box>
          </Box>
        )}

        {/* ── Step 2 ── */}
        {step === 2 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Ready to apply changes
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
              {newCount > 0 && (
                <Chip label={`${newCount} new stakeholder${newCount !== 1 ? 's' : ''}`} color="success" />
              )}
              {updateCount > 0 && (
                <Chip label={`${updateCount} update${updateCount !== 1 ? 's' : ''}`} color="info" />
              )}
            </Box>
            {excluded.size > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {excluded.size} row{excluded.size !== 1 ? 's' : ''} skipped.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Click <strong>Apply</strong> to save.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {step > 0 && <Button onClick={() => setStep(s => s - 1)} disabled={loading}>Back</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>

        {step === 0 && (
          <Button variant="contained" onClick={handleUpload} disabled={!file || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <UploadFileIcon />}>
            Parse File
          </Button>
        )}
        {step === 1 && (
          <Button variant="contained" onClick={() => setStep(2)} disabled={includedRows.length === 0}>
            Continue ({includedRows.length})
          </Button>
        )}
        {step === 2 && (
          <Button variant="contained" color="success" onClick={handleConfirm}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleOutlineIcon />}>
            Apply
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
